<!--
  PickupTimeField: one compact chip reading "Now" (ASAP) or the picked time
  ("Sat 31 Oct, 14:30"). Tapping it opens a small popover with "Now" and the
  date and time picker. Replaces the "As soon as possible / Schedule" radio
  pair; the values (v-model:mode, v-model:time) are the same.

  Three fixes over the old picker (all in utils/orderTime):
  - today stays selectable: disabled-date compares the calendar date with
    today in Nairobi, not with new Date();
  - clearing the picker goes back to ASAP instead of sending 1970-01-01;
  - the value is the Nairobi wall-clock string (value-format), so nothing is
    compared as a Date against a string or shifted by the browser's zone.

  Picking a time sets mode "scheduled" and the time together; "Now" sets
  mode "asap" (the time is left as it was, as the radio did). The picker
  inside the popover is not teleported, so using it doesn't count as a
  click outside the popover.

  The parent passes `now`, which it refreshes, so the "at least 15 minutes"
  message stays true while the form is open.

  Dedicated (`dedicated`): Schedule only, so there is no "Now" and the chip
  starts as "Choose pickup time"; clearing the picker leaves it empty
  rather than going back to ASAP. The time must be 2 hours to 30 days ahead
  (orders refuses anything else with dedicated_lead_time). The parent keeps
  mode "scheduled" for Dedicated.
-->
<script lang="ts" setup>
import { computed, ref } from "vue";
import { ArrowDown, Clock } from "@element-plus/icons-vue";
import {
  type PickupMode,
  PICKER_VALUE_FORMAT,
  PICKER_DISPLAY_FORMAT,
  SCHEDULE_MIN_LEAD_MINUTES,
  SCHEDULE_MAX_DAYS,
  DEDICATED_MIN_LEAD_MINUTES,
  isDisabledPickupDay,
  scheduleError,
  scheduleErrorMessage,
  formatBookedFor,
} from "../../utils/orderTime";

const props = defineProps<{
  mode: PickupMode;
  time: string;
  now: number;
  /** A Dedicated booking: Schedule only, 2 hours to 30 days ahead. */
  dedicated?: boolean;
}>();

const minLead = computed(() => (props.dedicated ? DEDICATED_MIN_LEAD_MINUTES : SCHEDULE_MIN_LEAD_MINUTES));

const emit = defineEmits<{
  (e: "update:mode", value: PickupMode): void;
  (e: "update:time", value: string): void;
}>();

// The picker opens on 12:00 for a new day, as the old Schedule tab did.
const defaultTime = new Date(2000, 1, 1, 12, 0, 0);

const open = ref(false);
const chip = ref<HTMLButtonElement | null>(null);

// Opening moves focus into the popover; Esc closes it and returns focus.
function focusInside() {
  const first = document.querySelector<HTMLElement>("#ptf-pop .ptf__now, #ptf-pop input");
  first?.focus();
}
// Tabbing out of the popover closes it, so it never sits open behind the
// keyboard user. The date picker's own panel is a separate popper: moving
// into it is not leaving.
function onPopFocusOut(e: FocusEvent) {
  const to = e.relatedTarget as HTMLElement | null;
  if (!to) return;
  if (to.closest("#ptf-pop, .ep-picker__popper, .ptf-popper") || to === chip.value) return;
  open.value = false;
}

function closeAndReturn() {
  open.value = false;
  chip.value?.focus();
}

function pickNow() {
  emit("update:mode", "asap");
  closeAndReturn();
}

function onTime(v: string | null | undefined) {
  if (!v) {
    // Cleared: back to ASAP rather than an empty or epoch time. Dedicated
    // has no ASAP, so it just waits for a new time.
    emit("update:time", "");
    if (!props.dedicated) emit("update:mode", "asap");
    return;
  }
  emit("update:time", v);
  if (props.mode !== "scheduled") emit("update:mode", "scheduled");
}

// When the picker is done (OK, or it closes on a new value), a usable time
// closes the popover; a refused one leaves it open with why.
function onPicked(v: string | null | undefined) {
  if (v && !scheduleError(v, props.now, minLead.value)) closeAndReturn();
}

const scheduled = computed(() => props.dedicated || props.mode === "scheduled");
const chipText = computed(() => {
  if (!scheduled.value) return "Now";
  return formatBookedFor(props.time) || "Choose pickup time";
});

const disabledDate = (d: Date) => isDisabledPickupDay(d, props.now, minLead.value);

// Shown once a time is picked; an empty schedule is reported by the
// Confirm button's reason line instead.
const error = computed(() => {
  if (!scheduled.value || !props.time) return "";
  const code = scheduleError(props.time, props.now, minLead.value);
  return code ? scheduleErrorMessage(code, minLead.value) : "";
});
</script>

<template>
  <div class="ptf">
    <span id="ptf-label" class="ods-label">Pickup time<span v-if="dedicated" class="ods-req" aria-hidden="true">*</span></span>
    <el-popover
      v-model:visible="open"
      trigger="click"
      placement="bottom-start"
      :width="296"
      :show-arrow="false"
      :offset="6"
      popper-class="ptf-popper"
      @after-enter="focusInside"
    >
      <template #reference>
        <button
          ref="chip"
          type="button"
          class="ptf__chip"
          :class="{ 'is-empty': scheduled && !time, 'is-invalid': !!error }"
          aria-haspopup="dialog"
          :aria-expanded="open"
          aria-controls="ptf-pop"
          aria-labelledby="ptf-label ptf-chip-text"
          :aria-required="dedicated ? 'true' : undefined"
          @keydown.esc="open = false"
        >
          <el-icon class="ptf__chip-icon" aria-hidden="true"><Clock /></el-icon>
          <span id="ptf-chip-text">{{ chipText }}</span>
          <el-icon class="ptf__chip-caret" aria-hidden="true"><ArrowDown /></el-icon>
        </button>
      </template>

      <div id="ptf-pop" class="ptf__pop" role="dialog" aria-label="Pickup time" @keydown.esc="closeAndReturn" @focusout="onPopFocusOut">
        <template v-if="!dedicated">
          <button
            type="button"
            class="ptf__now"
            :aria-pressed="mode === 'asap'"
            @click="pickNow"
          >
            <span class="ptf__now-title">Now</span>
            <span class="ptf__now-sub">As soon as possible</span>
          </button>
          <div class="ptf__or" aria-hidden="true"><span>or schedule</span></div>
        </template>

        <el-date-picker
          :model-value="(scheduled && time) || null"
          type="datetime"
          placeholder="Pick-up date and time"
          aria-label="Pick-up date and time"
          :aria-required="dedicated ? 'true' : undefined"
          :value-format="PICKER_VALUE_FORMAT"
          :format="PICKER_DISPLAY_FORMAT"
          :default-time="defaultTime"
          :disabled-date="disabledDate"
          :show-now="false"
          :teleported="false"
          size="large"
          class="ptf__picker"
          :class="{ 'is-invalid': !!error }"
          style="width:100%"
          @update:model-value="onTime"
          @change="onPicked"
        />
        <p v-if="dedicated" class="ods-help">
          Pick a time at least 2 hours from now, up to {{ SCHEDULE_MAX_DAYS }} days ahead. Nairobi time (EAT).
        </p>
        <p v-else class="ods-help">
          Nairobi time (EAT). At least {{ SCHEDULE_MIN_LEAD_MINUTES }} minutes ahead, up to {{ SCHEDULE_MAX_DAYS }} days. Drivers see this time when they accept.
        </p>
        <p v-if="error && open" class="ods-error">{{ error }}</p>
      </div>
    </el-popover>
    <p v-if="error" class="ods-error" role="status">{{ error }}</p>
  </div>
</template>

<style scoped src="./order-form.css"></style>
<style scoped>
.ptf { font-family: 'DM Sans', sans-serif; }

.ptf__chip {
  min-height: 40px;
  max-width: 100%;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
  color: #12284A;
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.ptf__chip:hover, .ptf__chip[aria-expanded="true"] { border-color: #12284A; }
.ptf__chip:focus-visible { outline: 2px solid #2d43ff; outline-offset: 2px; }
.ptf__chip.is-empty { color: #475569; font-weight: 500; border-style: dashed; }
.ptf__chip.is-invalid { border-color: #dc2626; }
.ptf__chip-icon { font-size: 15px; color: #64748b; }
.ptf__chip-caret { font-size: 12px; color: #94a3b8; }

.ptf__pop { font-family: 'DM Sans', sans-serif; padding: 4px; }
.ptf__now {
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}
.ptf__now:hover { border-color: #12284A; }
.ptf__now[aria-pressed="true"] { border-color: #12284A; background: #f1f5fb; }
.ptf__now:focus-visible { outline: 2px solid #2d43ff; outline-offset: 2px; }
.ptf__now-title { font-size: 0.86rem; font-weight: 700; color: #12284A; }
.ptf__now-sub { font-size: 0.74rem; color: #64748b; }
.ptf__or {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 8px;
  color: #94a3b8;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ptf__or::before, .ptf__or::after { content: ""; flex: 1; height: 1px; background: #e2e8f0; }

:deep(.ptf__picker .ep-input__wrapper) {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0 !important;
  background: white !important;
  box-shadow: none !important;
  height: 42px !important;
}
:deep(.ptf__picker .ep-input__wrapper.is-focus) { border-color: #12284A !important; }
:deep(.ptf__picker.is-invalid .ep-input__wrapper) { border-color: #dc2626 !important; }
:deep(.ptf__picker .ep-input__inner) {
  font-family: 'DM Sans', sans-serif !important;
  font-size: 0.84rem !important;
  color: #374151 !important;
}
</style>

<!-- The popover box is rendered by Element Plus outside this component's
     root, so its frame is styled globally (namespaced). -->
<style>
.ep-popper.ptf-popper {
  max-width: calc(100vw - 24px);
  padding: 12px !important;
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 12px 32px rgba(18, 40, 74, 0.16) !important;
}
</style>
